import { throwApiError } from '@/shared/utils/apiResponse';
import { apiGet, apiPatch, apiPost } from '@/shared/utils/requestBuilder';

import type {
  BotFlowsElegiveisPage,
  BotFlowsElegiveisQuery,
  ConversaCloseErrorResponse,
  InternalChatActiveListResult,
  InternalChatConversa,
  InternalChatConversaDetails,
  InternalChatLinha,
  InternalChatMensagem,
  InternalChatVinculo,
  ListConversationsParams,
  TipoVinculoEnum,
} from '../../types';

const unwrap = <T>(body: unknown): T => {
  if (typeof body === 'object' && body !== null && 'data' in body) {
    return (body as { data: T }).data;
  }
  return body as T;
};

const jsonOk = async <T>(response: Response, fallbackMsg: string): Promise<T> => {
  if (!response.ok) await throwApiError(response, fallbackMsg);
  const body = (await response.json()) as unknown;
  return unwrap<T>(body);
};

export const conversationsService = {
  list: async (params: ListConversationsParams): Promise<InternalChatActiveListResult> => {
    const queryParams: Record<string, string | number | boolean> = {};
    if (params.isArchived !== undefined) queryParams.isArchived = params.isArchived;
    if (params.canal) queryParams.canal = params.canal;
    if (params.entityId) queryParams.entityId = params.entityId;
    if (params.idResponsavel) queryParams.idResponsavel = params.idResponsavel;
    if (params.semResponsavel !== undefined) queryParams.semResponsavel = params.semResponsavel;
    if (params.apenasComPendencias !== undefined)
      queryParams.apenasComPendencias = params.apenasComPendencias;
    if (params.tipoVinculo) {
      queryParams.tipoVinculo = Array.isArray(params.tipoVinculo)
        ? params.tipoVinculo.join(',')
        : params.tipoVinculo;
    }
    if (params.apenasComMensagensPendentes !== undefined)
      queryParams.apenasComMensagensPendentes = params.apenasComMensagensPendentes;
    if (params.apenasSemMensagensPendentes !== undefined)
      queryParams.apenasSemMensagensPendentes = params.apenasSemMensagensPendentes;
    if (params.search) queryParams.search = params.search;
    if (params.withoutLink !== undefined) queryParams.withoutLink = params.withoutLink;
    if (params.hasNegotiation !== undefined) queryParams.hasNegotiation = params.hasNegotiation;
    if (params.excludeTipoVinculo) {
      queryParams.excludeTipoVinculo = Array.isArray(params.excludeTipoVinculo)
        ? params.excludeTipoVinculo.join(',')
        : params.excludeTipoVinculo;
    }
    if (params.incluirEncaminhadasSecretaria !== undefined)
      queryParams.incluirEncaminhadasSecretaria = params.incluirEncaminhadasSecretaria;
    if (params.limit !== undefined) queryParams.limit = params.limit;
    if (params.cursor) queryParams.cursor = params.cursor;
    if (params.mode) queryParams.mode = params.mode;
    if (params.tipoTelefone) {
      queryParams.tipoTelefone = Array.isArray(params.tipoTelefone)
        ? params.tipoTelefone.join(',')
        : params.tipoTelefone;
    }
    if (params.sort) queryParams.sort = params.sort;
    if (params.aguardandoUsuario !== undefined)
      queryParams.aguardandoUsuario = params.aguardandoUsuario;
    if (params.includeSegmentCounts !== undefined)
      queryParams.includeSegmentCounts = params.includeSegmentCounts;

    const response = await apiGet('/conversas', queryParams);
    return jsonOk<InternalChatActiveListResult>(response, 'Erro ao carregar conversas');
  },

  details: async (
    conversaId: string,
    options?: { includeJuridico?: boolean; includeSecondary?: boolean },
  ): Promise<InternalChatConversaDetails> => {
    const queryParams: Record<string, string | boolean> = {};
    if (options?.includeJuridico !== undefined)
      queryParams.includeJuridico = options.includeJuridico;
    if (options?.includeSecondary !== undefined)
      queryParams.includeSecondary = options.includeSecondary;
    const response = await apiGet(`/conversas/${conversaId}/details`, queryParams);
    return jsonOk<InternalChatConversaDetails>(response, 'Erro ao carregar conversa');
  },

  close: async (conversaId: string): Promise<InternalChatConversa> => {
    const response = await apiPost(`/conversas/${conversaId}/close`, { motivo: 'MANUAL' });
    if (response.status === 409) {
      const payload = (await response.json().catch(() => ({}))) as ConversaCloseErrorResponse;
      if (payload.error?.code !== 'CONVERSA_JA_FINALIZADA') {
        throw new Error(payload.error?.message || 'Não foi possível finalizar a conversa');
      }
      const details = await conversationsService.details(conversaId);
      if (!details.isArchived) {
        throw new Error(payload.error?.message || 'Não foi possível finalizar a conversa');
      }
      return details;
    }
    const data = await jsonOk<InternalChatConversa | { conversa: InternalChatConversa }>(
      response,
      'Não foi possível finalizar a conversa',
    );
    if (typeof data === 'object' && data !== null && 'conversa' in data && data.conversa) {
      return data.conversa;
    }
    return data as InternalChatConversa;
  },

  unarchive: async (conversaId: string): Promise<InternalChatConversa> => {
    const response = await apiPost(`/conversas/${conversaId}/unarchive`);
    return jsonOk<InternalChatConversa>(response, 'Erro ao desarquivar conversa');
  },

  resumeTemplate: async (conversaId: string): Promise<InternalChatMensagem> => {
    const response = await apiPost(`/conversas/${conversaId}/resume-template`);
    return jsonOk<InternalChatMensagem>(response, 'Erro ao enviar template de retomada');
  },

  transfer: async (
    conversaId: string,
    payload: { novoResponsavelId: string; motivo?: string },
  ): Promise<InternalChatConversa> => {
    const response = await apiPost(`/conversas/${conversaId}/transfer`, payload);
    return jsonOk<InternalChatConversa>(response, 'Erro ao transferir conversa');
  },

  assignResponsavel: async (
    conversaId: string,
    responsavelId: string | null,
  ): Promise<InternalChatConversa> => {
    const response = await apiPost(`/conversas/${conversaId}/responsavel`, {
      responsavelId,
    });
    return jsonOk<InternalChatConversa>(response, 'Erro ao atribuir responsável');
  },

  linkToExisting: async (
    conversaId: string,
    payload: {
      entityType: string;
      tipo: TipoVinculoEnum;
      entityId: string;
      escritorioId: string;
      role?: 'PRIMARY' | 'SECONDARY';
    },
  ): Promise<{ vinculos: InternalChatVinculo[] }> => {
    const response = await apiPost(`/conversas/${conversaId}/link-to-existing`, payload);
    return jsonOk<{ vinculos: InternalChatVinculo[] }>(response, 'Erro ao vincular conversa');
  },

  enqueueExportPdf: async (conversaId: string): Promise<{ jobId: string }> => {
    const response = await apiPost(`/admin/chat/conversas/${conversaId}/export/pdf`, {});
    return jsonOk<{ jobId: string }>(response, 'Erro ao solicitar exportação');
  },

  getExportPdfStatus: async (
    conversaId: string,
    jobId: string,
  ): Promise<{ status: 'READY'; url: string } | { status: 'GENERATING' }> => {
    const response = await apiGet(`/admin/chat/conversas/${conversaId}/export/pdf/${jobId}`);
    return jsonOk<{ status: 'READY'; url: string } | { status: 'GENERATING' }>(
      response,
      'Erro ao consultar exportação',
    );
  },

  assumir: async (conversaId: string, reason?: string): Promise<InternalChatConversa> => {
    const response = await apiPost(`/conversas/${conversaId}/assumir`, reason ? { reason } : {});
    return jsonOk<InternalChatConversa>(response, 'Erro ao assumir atendimento');
  },

  retomarBot: async (conversaId: string): Promise<InternalChatConversa> => {
    const response = await apiPost(`/conversas/${conversaId}/retomar-bot`);
    return jsonOk<InternalChatConversa>(response, 'Erro ao retomar bot');
  },

  listLinhas: async (): Promise<InternalChatLinha[]> => {
    const response = await apiGet('/admin/chat/telefones-internos');
    const data = await jsonOk<{ items: InternalChatLinha[] }>(response, 'Erro ao carregar linhas');
    return data.items;
  },

  updateLinha: async (
    id: string,
    patch: { botFlowId?: string | null; autoAtendimento?: boolean },
  ): Promise<InternalChatLinha> => {
    const response = await apiPatch(`/admin/chat/telefones-internos/${id}`, patch);
    return jsonOk<InternalChatLinha>(response, 'Erro ao atualizar linha');
  },

  listBotFlowsElegiveis: async ({
    page,
    limit,
    search,
    ids,
  }: BotFlowsElegiveisQuery): Promise<BotFlowsElegiveisPage> => {
    const response = await apiGet('/admin/chat/bot-flows-elegiveis', {
      page,
      limit,
      ...(search ? { search } : {}),
      ...(ids?.length ? { ids: ids.join(',') } : {}),
    });
    return jsonOk<BotFlowsElegiveisPage>(response, 'Erro ao carregar bots elegíveis');
  },
};
